import React, { useState } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, HandCoins, Loader2, Send } from "lucide-react";
import { useI18n } from "../../i18n";
import { iconeMethode } from "./iconesMethodes";
import { declarerDejaRegle } from "../../lib/relancesApi";
import { METHODES_DECLARATION, aujourdhuiISO, dateCourte, declarationEnAttente, formatEuros } from "../../lib/relances";

// « J'ai déjà réglé » — côté DONATEUR, sur la page publique du lien de
// paiement. Le donateur SIGNALE, la synagogue DÉCIDE : rien ici ne marque la
// promesse payée. Le serveur enregistre une déclaration en attente, met les
// rappels en pause et prévient le gabbaï.
//
// Pensé pour un téléphone tenu d'une main : 4 gros boutons, date et note
// facultatives, un seul bouton « Envoyer » en bas.

// Déclaration de LA promesse du lien (jamais celle d'une autre promesse du
// même donateur : la page de la promesse B ne doit pas dire « vous nous avez
// signalé avoir réglé » à cause de la promesse A).
export function declarationDuLien(data) {
  if (!data) return null;
  if (data.donor_declaration) return data.donor_declaration;
  const principale = (data.pledges || []).find((p) => p.pledge_id === data.primary_pledge_id);
  return (principale && principale.donor_declaration) || null;
}

// Déclaration propre à une promesse listée (repli sur celle du lien pour la
// promesse principale, si le serveur ne la range qu'à la racine).
function declarationDe(p, data) {
  if (!p) return null;
  if (p.donor_declaration) return p.donor_declaration;
  return p.pledge_id === data?.primary_pledge_id ? data?.donor_declaration || null : null;
}

// Promesses que vise la déclaration : celles cochées sur la page, sinon
// celle du lien (si elle est encore à régler).
export function promessesConcernees(data, selection = []) {
  const items = data?.pledges || [];
  const cochees = items.filter((p) => selection.includes(p.pledge_id));
  if (cochees.length) return cochees;
  const principale = items.find((p) => p.pledge_id === data?.primary_pledge_id);
  return principale ? [principale] : [];
}

// « espèces, le 12/10 » dans la langue du donateur.
function useDetail() {
  const { t } = useI18n();
  return (decl) => {
    if (!decl) return "";
    const method = t(`declare.method_${decl.method || "autre"}`);
    const date = dateCourte(decl.paid_on);
    return date ? t("declare.detail_on", { method, date }) : method;
  };
}

// Encart affiché SUR la page de paiement : le lien « J'ai déjà réglé », ou,
// si une déclaration attend déjà la vérification du gabbaï, son rappel.
export function EncartDejaRegle({ data, onOpen }) {
  const { t } = useI18n();
  const detail = useDetail();
  const decl = declarationDuLien(data);
  const principale = (data?.pledges || []).find((p) => p.pledge_id === data?.primary_pledge_id);

  if (declarationEnAttente(decl)) {
    return (
      <div
        data-testid="declare-paid-pending"
        className="mt-5 p-4 rounded-2xl border border-emerald-300 bg-emerald-50 flex items-start gap-3"
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-emerald-800">{t("declare.pending_title")}</p>
          {principale && (
            <p className="text-xs font-medium text-emerald-800 mt-0.5">{ligneDe(t, principale)}</p>
          )}
          <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
            {t("declare.pending_body", { detail: capitaliser(detail(decl)) })}
          </p>
          <button
            type="button"
            onClick={onOpen}
            className="mt-2 h-9 text-xs font-semibold text-emerald-800 underline underline-offset-2"
          >
            {t("declare.pending_edit")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="declare-paid-link"
      className="mt-5 w-full p-4 rounded-2xl border-2 border-stone-200 bg-white hover:border-chk-navy/40 flex items-center gap-3 text-left transition-colors"
    >
      <span className="h-11 w-11 rounded-full bg-chk-bg flex items-center justify-center shrink-0">
        <HandCoins className="h-5 w-5 text-chk-gold" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-base font-semibold text-chk-navy">{t("declare.link_title")}</span>
        <span className="block text-xs text-chk-ash mt-0.5 leading-relaxed">{t("declare.link_hint")}</span>
      </span>
      <ChevronRight className="h-5 w-5 text-chk-ash shrink-0" />
    </button>
  );
}

// Écran « Comment avez-vous réglé ? » (remplace la carte de paiement).
// `selection` : ids des promesses cochées sur la page de paiement (par défaut
// seule celle du lien l'est). Vide → le serveur prend celle(s) du lien.
export default function DeclarerDejaRegle({ token, data, selection = [], onBack, onDeclared }) {
  const { t } = useI18n();
  const detail = useDetail();
  const items = data?.pledges || [];
  const cochees = items.filter((p) => selection.includes(p.pledge_id));
  const concernees = promessesConcernees(data, selection);
  // « En attente » d'après la déclaration de CHAQUE promesse visée — jamais
  // d'après celle d'une autre promesse.
  const enAttente = concernees.length > 0 && concernees.every((p) => declarationEnAttente(declarationDe(p, data)));
  const decl = enAttente ? declarationDe(concernees[0], data) : null;

  // Déclaration déjà en attente → on l'affiche au lieu du formulaire ; le
  // donateur peut la corriger (la nouvelle remplace l'ancienne).
  const [edition, setEdition] = useState(!enAttente);
  const [methode, setMethode] = useState(enAttente ? decl.method || "" : "");
  const [date, setDate] = useState(enAttente && decl.paid_on ? decl.paid_on : aujourdhuiISO());
  const [note, setNote] = useState("");
  const [etat, setEtat] = useState(""); // "" | "envoi" | "fait" | "deja"
  const [erreur, setErreur] = useState("");

  const envoyer = async (e) => {
    e.preventDefault();
    if (!methode || etat === "envoi") return;
    setEtat("envoi");
    setErreur("");
    try {
      await declarerDejaRegle(token, {
        method: methode,
        paid_on: date || null,
        note,
        pledge_ids: cochees.map((p) => p.pledge_id),
      });
      setEtat("fait");
      // Recharge les données du lien : l'encart « déjà signalé » apparaîtra
      // au retour sur la page de paiement.
      try { await onDeclared?.(); } catch { /* non bloquant */ }
    } catch (err) {
      const s = err?.response?.status;
      if (s === 409) {
        // Déjà réglée entre-temps (payée en ligne, ou confirmée par le
        // gabbaï) : tout est en ordre, on le dit — jamais « réessayez ».
        setEtat("deja");
        try { await onDeclared?.(); } catch { /* non bloquant */ }
        return;
      }
      setErreur(
        s === 429 ? t("declare.error_rate")
          : s === 403 ? t("declare.error_refused")
            : s === 404 || s === 410 ? t("declare.error_closed")
              : t("declare.error")
      );
      setEtat("");
    }
  };

  const retour = (
    <button
      type="button"
      onClick={onBack}
      className="h-11 text-sm text-chk-ash hover:text-chk-ink mb-2 inline-flex items-center gap-1"
    >
      <ChevronLeft className="h-4 w-4" /> {t("declare.back")}
    </button>
  );

  // Plus rien à signaler : aucune promesse à régler sur ce lien, ou la
  // promesse visée vient d'être réglée (409). Jamais de formulaire inutile.
  if (etat === "deja" || items.length === 0) {
    return (
      <div data-testid="declare-paid-nothing" role="status" className="w-full max-w-md chk-card p-8 sm:p-10 text-center chk-fade-up">
        <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="h-7 w-7 text-emerald-700" />
        </div>
        <h2 className="font-serif text-3xl text-chk-navy tracking-tight mb-3">{t("declare.nothing_title")}</h2>
        <p className="text-base text-chk-ash leading-relaxed mb-6">
          {etat === "deja" ? t("declare.error_settled") : t("declare.nothing_body")}
        </p>
        <button type="button" onClick={onBack} className="chk-btn-primary h-12 w-full text-base">
          {t("declare.done_back")}
        </button>
      </div>
    );
  }

  // Des promesses à régler, mais aucune visée (celle du lien est réglée et
  // rien n'est coché) : on renvoie choisir sur la page de paiement.
  if (concernees.length === 0) {
    return (
      <div data-testid="declare-paid-choose" className="w-full max-w-md chk-fade-up">
        {retour}
        <div className="chk-card p-6 sm:p-8">
          <h1 className="font-serif text-2xl sm:text-3xl text-chk-navy tracking-tight mb-2">{t("declare.title")}</h1>
          <p className="text-sm text-chk-ash leading-relaxed">{t("declare.choose_pledge")}</p>
        </div>
      </div>
    );
  }

  if (etat === "fait") {
    return (
      <div data-testid="declare-paid-done" role="status" className="w-full max-w-md chk-card p-8 sm:p-10 text-center chk-fade-up">
        <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="h-7 w-7 text-emerald-700" />
        </div>
        <h2 className="font-serif text-3xl text-chk-navy tracking-tight mb-3">{t("declare.done_title")}</h2>
        <p className="text-base text-chk-ash leading-relaxed mb-6">{t("declare.done_body")}</p>
        <button type="button" onClick={onBack} className="chk-btn-primary h-12 w-full text-base">
          {t("declare.done_back")}
        </button>
      </div>
    );
  }

  if (!edition) {
    return (
      <div data-testid="declare-paid-screen" className="w-full max-w-md chk-fade-up">
        {retour}
        <div className="chk-card p-6 sm:p-8">
          <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-6 w-6 text-emerald-700" />
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl text-chk-navy tracking-tight mb-2">
            {t("declare.pending_title")}
          </h1>
          {concernees.map((p) => (
            <p key={p.pledge_id} className="text-sm font-medium text-chk-ink mb-1">{ligneDe(t, p)}</p>
          ))}
          <p className="text-sm text-chk-ash leading-relaxed">
            {t("declare.pending_body", { detail: capitaliser(detail(decl)) })}
          </p>
          <button
            type="button"
            onClick={() => setEdition(true)}
            className="mt-5 h-12 w-full rounded-full border border-chk-navy text-chk-navy text-sm font-semibold hover:bg-chk-navy hover:text-white transition-colors"
          >
            {t("declare.pending_edit")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="declare-paid-screen" className="w-full max-w-md chk-fade-up">
      {retour}
      <form onSubmit={envoyer} className="chk-card p-5 sm:p-7">
        <h1 className="font-serif text-3xl text-chk-navy tracking-tight leading-tight">{t("declare.title")}</h1>
        {concernees.map((p) => (
          <p key={p.pledge_id} className="text-sm text-chk-ash mt-2">{ligneDe(t, p)}</p>
        ))}

        <div role="radiogroup" aria-label={t("declare.title")} className="grid grid-cols-2 gap-3 mt-5">
          {METHODES_DECLARATION.map((m) => {
            const Icone = iconeMethode(m.value);
            const actif = methode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                role="radio"
                aria-checked={actif}
                onClick={() => setMethode(m.value)}
                data-testid={`declare-method-${m.value}`}
                className={`h-20 rounded-2xl border-2 flex flex-col items-center justify-center gap-1.5 text-base font-semibold transition-colors ${
                  actif
                    ? "border-chk-navy bg-chk-navy text-white"
                    : "border-stone-200 bg-white text-chk-ink hover:border-chk-navy/40"
                }`}
              >
                <Icone className="h-6 w-6" />
                {t(`declare.method_${m.value}`)}
              </button>
            );
          })}
        </div>

        <label htmlFor="declare-date" className="chk-label block mt-6 mb-1.5">
          {t("declare.date_label")} <span className="normal-case tracking-normal text-stone-400">({t("declare.optional")})</span>
        </label>
        <input
          id="declare-date"
          type="date"
          value={date}
          max={aujourdhuiISO()}
          onChange={(e) => setDate(e.target.value)}
          data-testid="declare-date"
          className="chk-input h-12 w-full text-base"
        />

        <label htmlFor="declare-note" className="chk-label block mt-5 mb-1.5">
          {t("declare.note_label")} <span className="normal-case tracking-normal text-stone-400">({t("declare.optional")})</span>
        </label>
        <textarea
          id="declare-note"
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("declare.note_placeholder")}
          data-testid="declare-note"
          className="chk-input w-full text-base"
        />

        {erreur && (
          <p role="alert" className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            {erreur}
          </p>
        )}

        <button
          type="submit"
          disabled={!methode || etat === "envoi"}
          data-testid="declare-submit"
          className="mt-6 h-14 w-full rounded-full bg-chk-navy text-white text-base font-semibold hover:bg-chk-navy2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {etat === "envoi" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          {t("declare.submit")}
        </button>
        {!methode && (
          <p className="text-xs text-chk-ash text-center mt-2">{t("declare.choose_first")}</p>
        )}
      </form>
    </div>
  );
}

const capitaliser = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// « Pour : Kiddouch — 180 € » (reste dû).
function ligneDe(t, p) {
  return t("declare.for_pledge", {
    motive: p.motive || "",
    amount: formatEuros(p.amount_due_total ?? p.total_amount),
  });
}

// Pastille sur une ligne de la page de paiement : « Signalé réglé le 12/10 ».
export function PastilleSignale({ pledge }) {
  const { t } = useI18n();
  const decl = pledge && pledge.donor_declaration;
  if (!declarationEnAttente(decl)) return null;
  const date = dateCourte(decl.paid_on);
  return (
    <span
      data-testid={`declare-badge-${pledge.pledge_id}`}
      className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800"
    >
      <CheckCircle2 className="h-3 w-3" />
      {date ? t("declare.line_badge", { date }) : t("declare.line_badge_nodate")}
    </span>
  );
}
